import { Button, Col, ModalV2, Row, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useState } from 'react';
import styled from 'styled-components';
import RewardTypeForm from './RewardTypeForm';

// Icon images
import iconTicket from 'assets/images/icon/icon-ticket.png';
import iconStadium from 'assets/images/icon/icon-stadium.png';
import iconRaffle from 'assets/images/icon/icon-raffle.png';
import iconGold from 'assets/images/icon/icon-gold.png';
import iconMail from 'assets/images/icon/icon-mail.png';
import iconSeat from 'assets/images/icon/icon-seat.png';
import iconJersey from 'assets/images/icon/icon-jersey.png';
import iconSilverGold from 'assets/images/icon/icon-silver-gold.png';
import iconMegaphone from 'assets/images/icon/icon-megaphone.png';
import iconAllTiers from 'assets/images/icon/icon-all-tiers.png';
import iconSeatPremium from 'assets/images/icon/icon-seat-premium.png';
import iconGift from 'assets/images/icon/icon-gift.png';
import iconStar from 'assets/images/icon/icon-star.png';

const DetailImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  object-fit: cover;
  background: ${theme.GREY_20};
`;

const IconPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  background: ${theme.GREY_20};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
`;

const Section = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${theme.GREY_20};
`;

const TierCard = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background: ${(props) => {
    if (props.tier === 'gold') return '#FFD70015';
    if (props.tier === 'silver') return '#C0C0C015';
    return '#CD7F3215';
  }};
  border: 1px solid ${(props) => {
    if (props.tier === 'gold') return '#FFD70040';
    if (props.tier === 'silver') return '#C0C0C040';
    return '#CD7F3240';
  }};
  flex: 1;
`;

const TierEmoji = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇'
};

const NoteItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: ${theme.GREY_10};
  border-radius: 6px;
  margin-bottom: 8px;
  gap: 10px;
`;

const NoteIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const ICON_IMAGES = {
  ticket: iconTicket,
  stadium: iconStadium,
  raffle: iconRaffle,
  gold: iconGold,
  mail: iconMail,
  seat: iconSeat,
  jersey: iconJersey,
  'silver-gold': iconSilverGold,
  megaphone: iconMegaphone,
  'all-tiers': iconAllTiers,
  'seat-premium': iconSeatPremium,
  gift: iconGift,
  star: iconStar
};

const RewardTypeDetailModal = ({ item, team, existingItems, visible, onClose, onUpdate, onDelete }) => {
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this reward type?')) return;

    setDeleting(true);
    try {
      const res = await Api.delete(`/admin/rewardType/delete/${item._id}`);
      if (res) {
        onDelete(item._id);
      }
    } catch (e) {
      console.error(e);
      alert('Failed to delete reward type');
    }
    setDeleting(false);
  };

  const handleEditSuccess = () => {
    setEditMode(false);
    onUpdate();
  };

  if (!item) return null;

  // Edit mode - show form
  if (editMode) {
    return (
      <ModalV2
        visible={visible}
        closeModal={() => setEditMode(false)}
        width="700px"
        header={
          <Text h3>Edit Reward Type</Text>
        }
      >
        <RewardTypeForm
          team={team}
          initialData={item}
          existingItems={existingItems}
          isEditing={true}
          onSuccess={handleEditSuccess}
          onCancel={() => setEditMode(false)}
        />
      </ModalV2>
    );
  }

  // Detail view
  return (
    <ModalV2
      visible={visible}
      closeModal={onClose}
      width="700px"
      header={
        <Row between center wid="100%">
          <Text h3>Reward Type Detail</Text>
          <Row center>
            <Button
              text={deleting ? 'Deleting...' : 'Delete'}
              icon="delete"
              onClick={handleDelete}
              bordered
              col={theme.RED}
              marg="0 10px 0 0"
              disabled={deleting}
              style={{ width: 'fit-content', padding: '6px 16px', borderColor: theme.RED }}
            />
            <Button
              text="Edit"
              icon="edit"
              onClick={() => setEditMode(true)}
              bordered
              marg="0 40px 0 0"
              style={{ width: 'fit-content', padding: '6px 16px' }}
            />
          </Row>
        </Row>
      }
    >
      <Col pad="0 0 20px 0">
        {/* Header with Image */}
        <Row start gap="20px">
          {item.image ? (
            <DetailImage src={item.image} alt={item.name} />
          ) : (
            <IconPlaceholder>
              <span style={{ fontSize: 48 }}>🎁</span>
            </IconPlaceholder>
          )}
          <Col>
            <Row center gap="10px" marg="0 0 8px">
              <Text h4>{item.name}</Text>
              <Text
                style={{
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 12,
                  background: item.isActive ? theme.SUCCESS + '20' : theme.GREY_30,
                  color: item.isActive ? theme.SUCCESS : theme.GREY_60
                }}
              >
                {item.isActive ? 'Active' : 'Inactive'}
              </Text>
            </Row>
            <Text c1 col={theme.GREY_60} marg="0 0 8px">
              Type: {item.type === 'raffle' ? 'Raffle' : 'Direct'}
            </Text>
            <Text c1 col={theme.GREY_60}>
              Display Order: {item.displayOrder || 0}
            </Text>
          </Col>
        </Row>

        {/* Description */}
        <Section>
          <Text b2 med marg="0 0 8px">Short Description</Text>
          <Text col={theme.GREY_80}>{item.description || '-'}</Text>
        </Section>

        {item.detailedDescription && (
          <Section>
            <Text b2 med marg="0 0 8px">Detailed Description</Text>
            <Text col={theme.GREY_80} style={{ whiteSpace: 'pre-wrap' }}>
              {item.detailedDescription}
            </Text>
          </Section>
        )}

        {/* Eligibility */}
        <Section>
          <Text b2 med marg="0 0 12px">Eligibility by Tier</Text>
          <Row gap="12px">
            {['bronze', 'silver', 'gold'].map((tier) => {
              const tierData = item.eligibility?.[tier];
              const isEligible = tierData?.eligible;
              return (
                <TierCard key={tier} tier={tier}>
                  <Row center gap="8px" marg="0 0 8px">
                    <Text style={{ fontSize: 20 }}>{TierEmoji[tier]}</Text>
                    <Text med style={{ textTransform: 'capitalize' }}>{tier}</Text>
                  </Row>
                  <Text c1 col={isEligible ? theme.SUCCESS : theme.GREY_50}>
                    {isEligible ? 'Eligible' : 'Not Eligible'}
                  </Text>
                  {isEligible && tierData?.slots > 0 && (
                    <Text c1 col={theme.GREY_60} marg="4px 0 0">
                      Slots: {tierData.slots}
                    </Text>
                  )}
                  {isEligible && tierData?.description && (
                    <Text c1 col={theme.GREY_60} marg="4px 0 0">
                      {tierData.description}
                    </Text>
                  )}
                </TierCard>
              );
            })}
          </Row>
        </Section>

        {/* Availability Notes */}
        {item.availabilityNotes?.length > 0 && (
          <Section>
            <Text b2 med marg="0 0 12px">
              Availability Notes ({item.availabilityNotes.length})
            </Text>
            {item.availabilityNotes.map((note, index) => (
              <NoteItem key={index}>
                <NoteIcon src={ICON_IMAGES[note.icon] || iconTicket} alt={note.icon} />
                <Text col={theme.GREY_80}>{note.text}</Text>
              </NoteItem>
            ))}
          </Section>
        )}
      </Col>
    </ModalV2>
  );
};

export default RewardTypeDetailModal;
